import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserRole, getUserDisplayName, getUserEmail } from '../utils/roleUtils';
import EmployeeImageUpload from './EmployeeImageUpload';
import { API_ENDPOINTS } from '../config/apiRouting';
import './UserProfile.css';

interface UserProfileProps {
  user: any;
}

interface EmployeeData {
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  storeId?: string;
  storeName?: string;
  profileImageUrl?: string;
  phone?: string;
  hireDate?: string;
}

const UserProfile: React.FC<UserProfileProps> = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [employeeData, setEmployeeData] = useState<EmployeeData | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  
  const { getIdToken, session } = useAuth();
  const userRole = getUserRole(user, session);
  const userName = getUserDisplayName(user, session);
  const userEmail = getUserEmail(user, session);

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const idToken = await getIdToken();
      if (!idToken) {
        throw new Error('No authentication token');
      }

      // First, find the employee by email
      const response = await fetch(`${API_ENDPOINTS.employees}?email=${encodeURIComponent(userEmail)}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch employee data');
      }

      const data = await response.json();
      const employees = data.data || data.employees || [];
      
      if (employees.length > 0) {
        const employee = employees[0];
        setEmployeeData(employee);
        setPhoneNumber(employee.phone || '');
      } else {
        // Create basic employee data from user info
        setEmployeeData({
          employeeId: user.username || 'unknown',
          firstName: userName.split(' ')[0] || 'Unknown',
          lastName: userName.split(' ')[1] || 'User',
          email: userEmail,
          role: userRole,
        });
      }
    } catch (err) {
      console.error('Error fetching employee data:', err);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpdate = async (imageUrl: string) => {
    if (!employeeData) return;

    try {
      setSaving(true);
      const idToken = await getIdToken();
      
      const response = await fetch(`${API_ENDPOINTS.employees}/${employeeData.employeeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...employeeData,
          profileImageUrl: imageUrl,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update profile photo');
      }

      setEmployeeData({ ...employeeData, profileImageUrl: imageUrl });
      setSuccess('Profile photo updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating profile photo:', err);
      setError('Failed to update profile photo');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePhone = async () => {
    if (!employeeData) return;

    try {
      setSaving(true);
      setError(null);
      const idToken = await getIdToken();
      
      const response = await fetch(`${API_ENDPOINTS.employees}/${employeeData.employeeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...employeeData,
          phone: phoneNumber,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update phone number');
      }

      setEmployeeData({ ...employeeData, phone: phoneNumber });
      setEditMode(false);
      setSuccess('Phone number updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating phone number:', err);
      setError('Failed to update phone number');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="user-profile-container">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="user-profile-container">
      <div className="user-profile-header">
        <h1>My Profile</h1>
        <p className="subtitle">Manage your account information and profile photo</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <div className="user-profile-content">
        <div className="profile-section">
          <div className="profile-photo-section">
            <h2>Profile Photo</h2>
            <div className="current-photo">
              {employeeData?.profileImageUrl ? (
                <img 
                  src={employeeData.profileImageUrl} 
                  alt={userName}
                  className="profile-image"
                />
              ) : (
                <div className="profile-image-placeholder">
                  <span>{userName.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>
            {employeeData && (
              <EmployeeImageUpload
                employeeId={employeeData.employeeId}
                employeeName={userName}
                currentImageUrl={employeeData.profileImageUrl}
                onImageUpdated={handlePhotoUpdate}
              />
            )}
          </div>

          <div className="profile-info-section">
            <h2>Account Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Name</label>
                <div className="info-value">{userName}</div>
              </div>
              
              <div className="info-item">
                <label>Email</label>
                <div className="info-value">{userEmail}</div>
              </div>
              
              <div className="info-item">
                <label>Role</label>
                <div className="info-value">
                  {userRole === 'super_admin' ? 'Super Admin' : 
                   userRole === 'system_admin' ? 'System Admin' : 
                   userRole === 'enterprise_admin' ? 'Enterprise Admin' : 
                   userRole === 'manager' ? 'Manager' : 'Employee'}
                </div>
              </div>

              {employeeData?.storeName && (
                <div className="info-item">
                  <label>Store</label>
                  <div className="info-value">{employeeData.storeName}</div>
                </div>
              )}

              <div className="info-item">
                <label>Phone Number</label>
                <div className="info-value">
                  {editMode ? (
                    <div className="phone-edit">
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="Enter phone number"
                        className="phone-input"
                      />
                      <button 
                        onClick={handleSavePhone} 
                        disabled={saving}
                        className="btn-save"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button 
                        onClick={() => {
                          setEditMode(false);
                          setPhoneNumber(employeeData?.phone || '');
                        }}
                        className="btn-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="phone-display">
                      <span>{employeeData?.phone || 'Not set'}</span>
                      <button 
                        onClick={() => setEditMode(true)}
                        className="btn-edit"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {employeeData?.hireDate && (
                <div className="info-item">
                  <label>Hire Date</label>
                  <div className="info-value">
                    {new Date(employeeData.hireDate).toLocaleDateString()}
                  </div>
                </div>
              )}

              <div className="info-item">
                <label>Employee ID</label>
                <div className="info-value">{employeeData?.employeeId || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;